import * as THREE from "three";

function hash(ix: number, iy: number, seed: number) {
  let n = Math.imul(ix, 374761393) ^ Math.imul(iy, 668265263) ^ Math.imul(seed, 1274126177);
  n = Math.imul(n ^ (n >>> 13), 1274126177);
  return ((n ^ (n >>> 16)) >>> 0) / 4294967296;
}

function vnoise(x: number, y: number, seed: number) {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const fx = x - x0;
  const fy = y - y0;
  const sx = fx * fx * (3 - 2 * fx);
  const sy = fy * fy * (3 - 2 * fy);
  const a = hash(x0, y0, seed);
  const b = hash(x0 + 1, y0, seed);
  const c = hash(x0, y0 + 1, seed);
  const d = hash(x0 + 1, y0 + 1, seed);
  return a + (b - a) * sx + (c - a) * sy + (a - b - c + d) * sx * sy;
}

function fbm(x: number, y: number, seed: number, oct = 5) {
  let a = 0,
    amp = 0.5,
    f = 1,
    n = 0;
  for (let i = 0; i < oct; i++) {
    a += vnoise(x * f, y * f, seed + i * 19) * amp;
    n += amp;
    amp *= 0.5;
    f *= 2;
  }
  return a / n;
}

function canvas(size: number) {
  const c = document.createElement("canvas");
  c.width = c.height = size;
  const ctx = c.getContext("2d", { willReadFrequently: true })!;
  return { c, ctx };
}

function toNormal(height: Float32Array, size: number, strength = 2.4) {
  const data = new Uint8Array(size * size * 4);
  const wrap = (v: number) => (v + size) % size;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const hL = height[y * size + wrap(x - 1)];
      const hR = height[y * size + wrap(x + 1)];
      const hD = height[wrap(y - 1) * size + x];
      const hU = height[wrap(y + 1) * size + x];
      let nx = (hL - hR) * strength;
      let ny = (hD - hU) * strength;
      let nz = 1;
      const len = Math.hypot(nx, ny, nz) || 1;
      nx /= len;
      ny /= len;
      nz /= len;
      const i = (y * size + x) * 4;
      data[i] = (nx * 0.5 + 0.5) * 255;
      data[i + 1] = (ny * 0.5 + 0.5) * 255;
      data[i + 2] = (nz * 0.5 + 0.5) * 255;
      data[i + 3] = 255;
    }
  }
  const tex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.needsUpdate = true;
  tex.colorSpace = THREE.NoColorSpace;
  return tex;
}

function albedoFrom(fn: (x: number, y: number) => [number, number, number], size: number, roughFn?: (x: number, y: number) => number): PBRSet {
  const { c, ctx } = canvas(size);
  const img = ctx.createImageData(size, size);
  const height = new Float32Array(size * size);
  const rough = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const u = x / size;
      const v = y / size;
      const [r, g, b] = fn(u, v);
      const i = (y * size + x) * 4;
      img.data[i] = r * 255;
      img.data[i + 1] = g * 255;
      img.data[i + 2] = b * 255;
      img.data[i + 3] = 255;
      const h = (r + g + b) / 3;
      height[y * size + x] = h;
      const rg = roughFn ? roughFn(u, v) : 1 - h * 0.45;
      rough[i] = rough[i + 1] = rough[i + 2] = Math.max(8, Math.min(255, rg * 255));
      rough[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  const map = new THREE.CanvasTexture(c);
  map.wrapS = map.wrapT = THREE.RepeatWrapping;
  map.colorSpace = THREE.SRGBColorSpace;
  map.anisotropy = 8;
  map.needsUpdate = true;
  const rtex = new THREE.DataTexture(rough, size, size, THREE.RGBAFormat);
  rtex.wrapS = rtex.wrapT = THREE.RepeatWrapping;
  rtex.needsUpdate = true;
  rtex.colorSpace = THREE.NoColorSpace;
  const nrm = toNormal(height, size);
  nrm.anisotropy = 8;
  rtex.anisotropy = 8;
  return { map, normalMap: nrm, roughnessMap: rtex };
}

async function tryPhoto(url: string) {
  try {
    const loader = new THREE.TextureLoader();
    const t = await loader.loadAsync(url);
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.colorSpace = THREE.SRGBColorSpace;
    t.anisotropy = 8;
    t.needsUpdate = true;
    return t;
  } catch {
    return null;
  }
}

export type PBRSet = {
  map: THREE.Texture;
  normalMap: THREE.Texture;
  roughnessMap: THREE.Texture;
};



export type TextureLibrary = {
  concrete: PBRSet;
  asphalt: PBRSet;
  rust: PBRSet;
  paintGray: PBRSet;
  paintOrange: PBRSet;
  paintTeal: PBRSet;
  diamond: PBRSet;
  hazard: PBRSet;
  plaster: PBRSet;
  waterN: THREE.Texture;
  muzzle: THREE.Texture;
  smoke: THREE.Texture;
  spark: THREE.Texture;
  soft: THREE.Texture;
};

function spriteRadial(inner: string, outer: string, spikes = 0) {
  const { c, ctx } = canvas(256);
  ctx.clearRect(0, 0, 256, 256);
  const g = ctx.createRadialGradient(128, 128, 4, 128, 128, 120);
  g.addColorStop(0, inner);
  g.addColorStop(0.35, outer);
  g.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 256, 256);
  if (spikes) {
    ctx.save();
    ctx.translate(128, 128);
    ctx.fillStyle = "rgba(255,240,200,0.85)";
    for (let i = 0; i < spikes; i++) {
      ctx.rotate(Math.PI / spikes);
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(4, 110);
      ctx.lineTo(-4, 110);
      ctx.closePath();
      ctx.fill();
    }
    ctx.restore();
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.needsUpdate = true;
  return t;
}

export async function buildTextures(): Promise<TextureLibrary> {
  const concrete = albedoFrom(
    (u, v) => {
      const n = fbm(u * 8, v * 8, 11);
      const peb = vnoise(u * 70, v * 70, 3);
      const stain = fbm(u * 2.2, v * 2.2, 44);
      const g = 0.28 + n * 0.18 + peb * 0.06 - stain * 0.08;
      return [g * 0.96, g * 0.97, g];
    },
    512,
    (u, v) => 0.55 + fbm(u * 6, v * 6, 9) * 0.3
  );

  const asphalt = albedoFrom(
    (u, v) => {
      const n = fbm(u * 10, v * 10, 21);
      const speckle = hash((u * 180) | 0, (v * 180) | 0, 8);
      const g = 0.1 + n * 0.08 + speckle * 0.05;
      return [g * 0.95, g * 0.96, g];
    },
    512,
    (u, v) => 0.62 + fbm(u * 8, v * 8, 2) * 0.25
  );

  const rust = albedoFrom(
    (u, v) => {
      const n = fbm(u * 6, v * 6, 71);
      const blot = fbm(u * 3, v * 3, 90);
      const r = 0.35 + blot * 0.4 + n * 0.1;
      const g = 0.16 + blot * 0.12;
      const b = 0.08 + n * 0.05;
      return [r, g, b];
    },
    512,
    (u, v) => 0.48 + fbm(u * 5, v * 5, 4) * 0.4
  );

  const paint = (hue: [number, number, number], seed: number) =>
    albedoFrom(
      (u, v) => {
        const dirt = fbm(u * 5, v * 5, seed);
        const scratch = vnoise(u * 90, v * 14, seed + 4);
        const k = 0.78 - dirt * 0.22 - (scratch > 0.82 ? 0.15 : 0);
        return [hue[0] * k, hue[1] * k, hue[2] * k];
      },
      512,
      (u, v) => 0.38 + fbm(u * 7, v * 7, seed) * 0.35
    );

  const diamond = albedoFrom(
    (u, v) => {
      const pu = ((u * 10) % 1) - 0.5;
      const pv = ((v * 10) % 1) - 0.5;
      const d = Math.abs(pu) + Math.abs(pv);
      const plate = d < 0.42 ? 0.22 : 0.16;
      const n = fbm(u * 8, v * 8, 5) * 0.05;
      const g = plate + n;
      return [g, g * 1.02, g * 1.05];
    },
    512,
    (u, v) => {
      const pu = ((u * 10) % 1) - 0.5;
      const pv = ((v * 10) % 1) - 0.5;
      return Math.abs(pu) + Math.abs(pv) < 0.42 ? 0.35 : 0.55;
    }
  );

  const hazard = albedoFrom(
    (u, v) => {
      const t = (u + v) * 6;
      const band = Math.abs(t % 2) < 1;
      const dirt = fbm(u * 8, v * 8, 12) * 0.18;
      return band ? [0.82 - dirt, 0.62 - dirt, 0.08] : [0.08, 0.08, 0.08];
    },
    512,
    () => 0.45
  );

  const plaster = albedoFrom(
    (u, v) => {
      const n = fbm(u * 7, v * 7, 33);
      const g = 0.42 + n * 0.12;
      return [g * 0.95, g * 0.97, g];
    },
    512
  );

  const waterN = toNormal(
    Float32Array.from({ length: 256 * 256 }, (_, i) => {
      const x = (i % 256) / 256;
      const y = Math.floor(i / 256) / 256;
      return fbm(x * 6, y * 6, 101) * 0.6 + vnoise(x * 18, y * 18, 7) * 0.4;
    }),
    256,
    4.2
  );
  waterN.wrapS = waterN.wrapT = THREE.RepeatWrapping;

  const photoC = await tryPhoto("/textures/concrete.jpg");
  const photoA = await tryPhoto("/textures/asphalt.jpg");
  const photoO = await tryPhoto("/textures/container_orange.jpg");
  const photoT = await tryPhoto("/textures/container_teal.jpg");
  if (photoC) concrete.map = photoC;
  if (photoA) asphalt.map = photoA;
  if (photoO) (paint([0.86, 0.38, 0.12], 17) as PBRSet).map = photoO;

  const paintOrange = paint([0.86, 0.38, 0.12], 17);
  const paintTeal = paint([0.18, 0.42, 0.48], 29);
  const paintGray = paint([0.45, 0.47, 0.5], 8);
  if (photoO) paintOrange.map = photoO;
  if (photoT) paintTeal.map = photoT;

  const muzzlePhoto = await tryPhoto("/sprites/muzzle.jpg");
  const sparkPhoto = await tryPhoto("/sprites/spark.jpg");

  return {
    concrete,
    asphalt,
    rust,
    paintGray,
    paintOrange,
    paintTeal,
    diamond,
    hazard,
    plaster,
    waterN,
    muzzle: muzzlePhoto ?? spriteRadial("rgba(255,255,240,1)", "rgba(255,140,40,0.7)", 10),
    smoke: spriteRadial("rgba(190,190,190,0.55)", "rgba(80,80,80,0.15)"),
    spark: sparkPhoto ?? spriteRadial("rgba(255,230,160,1)", "rgba(255,120,20,0.5)", 16),
    soft: spriteRadial("rgba(255,255,255,0.9)", "rgba(255,255,255,0)"),
  };
}
